<!--
  The implementation is mostly based on the `details` element with the usage of
  `summary` and `details`. Unfortunately that native element lacks animations
  and a way to properly style it.

  See: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/details
-->
<template>
  <div
    class="wl-base-expandable"
    :class="{ 'is-open': internalIsOpen, 'is-resizing': isResizingByToggle }"
  >
    <div
      ref="summaryRef"
      class="wl-base-expandable-summary"
      :class="{ 'hover:cursor-pointer': !isDisabled }"
      v-if="$slots.summary"
    >
      <slot name="summary" :is-open="internalIsOpen" :toggle="handleToggle" />
    </div>
    <div
      class="wl-base-expandable--expandable"
      ref="expandableRef"
      :class="[
        expandableClasses,
        {
          'transition-[height] duration-300 ease-in-out': isResizingByToggle,
        },
      ]"
      :style="{ height: internalIsOpen ? `${height}px` : '0px' }"
      @click.stop
    >
      <div ref="detailsRef" v-if="hasBeenOpened">
        <slot name="details" />
      </div>
    </div>
    <slot name="suffix" />
  </div>
</template>

<script lang="ts" setup>
import { ref, toRefs, watch } from "vue";
import {
  type WLBaseExpandableProps,
  type WLBaseExpandableEmits,
} from "./types";
import { autoResetRef, useElementBounding } from "@vueuse/core";

const emit = defineEmits<WLBaseExpandableEmits>();

const props = withDefaults(defineProps<WLBaseExpandableProps>(), {
  expandableClasses: "overflow-hidden",
  initialState: () => ({
    isOpen: false,
  }),
});

const { initialState, isOpen } = toRefs(props);
const internalIsOpen = ref(initialState.value.isOpen);
const expandableRef = ref<HTMLElement | null>(null);
const detailsRef = ref<HTMLElement | null>(null);
const isResizingByToggle = autoResetRef(false, 500);

const hasBeenOpened = ref(internalIsOpen.value);

watch(isOpen, (value) => {
  isResizingByToggle.value = true;
  internalIsOpen.value = value;
});

watch(internalIsOpen, (value) => {
  if (value) {
    hasBeenOpened.value = true;
  }
});

// If performance issues occur due to too many resize calls find a more performant way in this PR: https://github.com/chargetrip/discover/pull/1158
const { height } = useElementBounding(detailsRef, {
  windowResize: false,
  windowScroll: false,
});

const handleToggle = (): void => {
  isResizingByToggle.value = true;
  internalIsOpen.value = !internalIsOpen.value;
  emit("isOpen", internalIsOpen.value);
};
</script>
